import React from 'react';
import { Card } from '../shadcn-ui/card';
import { useNavigate } from 'react-router-dom';
import { getDryingBedInfoByBedPosition, getQRResult } from '@/network/api.service';
import { toast } from 'sonner';

const formatted = date =>
  new Date(date)
    .toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .replace('/', '-')
    .replace(',', ' ')
    .replace(' AM', 'AM') // attach AM
    .replace(' PM', 'PM');

const formatBedCode = (bed_number, bed_position) => {
  const prefix = bed_position === 'upper' ? 'U' : bed_position === 'middle' ? 'M' : 'L';
  return `${prefix}${String(bed_number).padStart(2, '0')}`;
};

const getOrdinalDay = day => {
  if (day == null) return null;

  if (day >= 11 && day <= 13) return `${day}th`;

  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const DryingBedCard = ({ bed }) => {
  const navigate = useNavigate();

  const navigateToBed = async (bed_number, bed_position) => {
    try {
      const response = await getDryingBedInfoByBedPosition(bed_position, bed_number);
      if (response.data.success) {
        navigate(response.data.data.redirect_url);
      }
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message);
    }
  };

  return (
    <Card className="w-full max-w-[114px] overflow-hidden rounded-xl md:max-w-36">
      {/* Upper bed (FIXED HEIGHT) */}
      <div onClick={() => navigateToBed(bed?.bed_number, 'UPPER')} className="h-24 max-h-24 p-2.5">
        <div className="mb-1 text-sm font-semibold">{formatBedCode(bed?.bed_number, 'upper')}</div>

        {bed?.upper_bed?.is_empty ? (
          <div className="text-sm text-muted-foreground">Empty</div>
        ) : (
          <div className="text-xs">
            <div className="font-semibold">{bed?.upper_bed?.lot_name?.split('_').slice(0, 2).join('_')}</div>
            <div className="flex items-center gap-1 text-secondary-foreground">
              {/* 10th day, 28% */}
              <div className="whitespace-nowrap">{getOrdinalDay(bed?.upper_bed?.days)} day,</div>
              {bed?.upper_bed?.moisture && <div>{bed?.upper_bed?.moisture && `${bed?.upper_bed?.moisture}%`}</div>}
            </div>
            {bed?.upper_bed?.last_check_time && (
              <div className="tracking-tight text-secondary-foreground">
                {formatted(bed?.upper_bed?.last_check_time)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-[#DEE1E6]" />

      {/* Middle bed (FIXED HEIGHT) */}
      <div onClick={() => navigateToBed(bed?.bed_number, 'MIDDLE')} className="h-24 max-h-24 p-2.5">
        <div className="mb-1 text-sm font-semibold">{formatBedCode(bed?.bed_number, 'middle')}</div>

        {!bed?.middle_bed?.lot_name ? (
          <div className="text-sm text-muted-foreground">Empty</div>
        ) : (
          <div className="text-xs">
            <div className="font-semibold">{bed?.middle_bed?.lot_name?.split('_').slice(0, 2).join('_')}</div>
            <div className="flex items-center gap-1 text-secondary-foreground">
              {/* 10th day, 28% */}
              <div className="whitespace-nowrap">{getOrdinalDay(bed?.middle_bed?.days)} day,</div>
              {bed?.middle_bed?.moisture && <div>{bed?.middle_bed?.moisture && `${bed?.middle_bed?.moisture}%`}</div>}
            </div>
            {bed?.middle_bed?.last_check_time && (
              <div className="tracking-tight text-secondary-foreground">
                {formatted(bed?.middle_bed?.last_check_time)}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Divider */}
      <div className="border-t border-[#DEE1E6]" />

      {/* Lower bed (AUTO HEIGHT) */}
      <div onClick={() => navigateToBed(bed?.bed_number, 'LOWER')} className="h-24 max-h-24 p-2.5">
        <div className="mb-1 text-sm font-semibold">{formatBedCode(bed?.bed_number, 'lower')}</div>

        {bed?.lower_bed?.is_empty ? (
          <div className="text-sm text-muted-foreground">Empty</div>
        ) : (
          <div className="text-xs">
            <div className="font-semibold">{bed?.lower_bed?.lot_name?.split('_').slice(0, 2).join('_')}</div>
            <div className="flex items-center gap-1 text-secondary-foreground">
              <div className="whitespace-nowrap">{getOrdinalDay(bed?.lower_bed?.days)} day,</div>
              {bed?.lower_bed?.moisture && <div>{bed?.lower_bed?.moisture && `${bed?.lower_bed?.moisture}%`}</div>}
            </div>
            {bed?.lower_bed?.last_check_time && (
              <div className="tracking-tight text-secondary-foreground">
                {formatted(bed?.lower_bed?.last_check_time)}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default DryingBedCard;
